export const API_KEY_SCOPES = [
  "contacts:read",
  "contacts:write",
  "messages:send",
  "reports:read",
  "webhooks:manage",
  "channels:read",
  "channels:write",
] as const;

export type ApiKeyScope = typeof API_KEY_SCOPES[number];

export type ApiKeyStatus = "active" | "expiring" | "expired" | "revoked";

export interface ApiKeyView {
  id: string;
  name: string;
  key_prefix: string;
  scopes: ApiKeyScope[];
  status: ApiKeyStatus;
  created_at: string;
  created_by_name: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_by_name: string | null;
  rotated_from_id: string | null;
  rotated_at: string | null;
}

export interface ApiKeyEvent {
  id: string;
  api_key_id: string | null;
  action: "created" | "rotated" | "revoked" | "imported" | "access_disabled" | "access_enabled";
  actor_name: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface ApiKeysListResponse {
  org_number: string | null;
  access_enabled: boolean;
  keys: ApiKeyView[];
  events: ApiKeyEvent[];
}

export const API_KEY_SCOPE_LABELS: Record<ApiKeyScope, { label: string; description: string }> = {
  "contacts:read": { label: "Leer contactos", description: "Consultar contactos, etiquetas y bajas." },
  "contacts:write": { label: "Editar contactos", description: "Crear y actualizar contactos, etiquetas y bajas." },
  "messages:send": { label: "Enviar mensajes", description: "Enviar plantillas de WhatsApp." },
  "reports:read": { label: "Leer reportes", description: "Exportar conversaciones, mensajes y métricas." },
  "webhooks:manage": { label: "Gestionar webhooks", description: "Crear, cambiar y probar suscripciones a eventos." },
  "channels:read": { label: "Leer canales", description: "Consultar la política de envío y la salud de los números." },
  "channels:write": { label: "Configurar canales", description: "Cambiar la política de envío de los números." },
};

export const API_KEY_STATUS_LABELS: Record<ApiKeyStatus, string> = {
  active: "Activa",
  expiring: "En traslape",
  expired: "Vencida",
  revoked: "Revocada",
};

export const API_KEY_EVENT_LABELS: Record<ApiKeyEvent["action"], string> = {
  created: "Creó la clave",
  rotated: "Rotó la clave",
  revoked: "Revocó la clave",
  imported: "Clave existente migrada con todos los permisos",
  access_disabled: "Soporte deshabilitó el acceso API",
  access_enabled: "Soporte reactivó el acceso API",
};

export const OVERLAP_OPTIONS: ReadonlyArray<{ hours: number; label: string }> = [
  { hours: 0, label: "Sin traslape (la anterior deja de servir ya)" },
  { hours: 1, label: "1 hora" },
  { hours: 24, label: "24 horas (recomendado)" },
  { hours: 72, label: "3 días" },
  { hours: 168, label: "7 días" },
];

export const DEFAULT_OVERLAP_HOURS = 24;

export function sortScopes(scopes: readonly string[]): ApiKeyScope[] {
  return API_KEY_SCOPES.filter((s) => scopes.includes(s));
}

export function isUsableKey(key: Pick<ApiKeyView, "status">): boolean {
  return key.status === "active" || key.status === "expiring";
}

export function canRotate(key: Pick<ApiKeyView, "status" | "rotated_at">): boolean {
  return key.status === "active" && !key.rotated_at;
}

export function canManageApiKeys(userRole: string | null | undefined, isSuperAdmin: boolean): boolean {
  return isSuperAdmin || userRole === "admin";
}
